<p-drawer
  [(visible)]="tasksSidebarVisible"
  position="right"
  [style]="{ width: '60em', 'max-width': '100%' }"
  class="tasks-sidebar"
  (onHide)="onHide()"
>
  <ng-template #header>
    <div class="drawerHeader flex">
      <h3 class="m-0 p-0">
        {{ 'reportTasks.sidebarTitle' | translate | titlecase }}
      </h3>
      <p-button (click)="closeAllTaskSockets()" class="reloadSocketsButton" [text]="true" severity="secondary">
        <ng-template #icon>
          <fa-icon [icon]="faArrowsRotate"></fa-icon>
        </ng-template>
      </p-button>
    </div>
  </ng-template>

  <section>
    <header class="mb-4 flex items-center justify-between">
      <h4 class="m-0 p-0">
        {{ ('reportTasks.pendingTasks' | translate | titlecase) + ' (' + (pendingTasks$ | async)?.length + ')' }}
      </h4>
      <div class="flex items-center justify-end"></div>
    </header>
    <app-agnostic-grids [dependencies]="(pendingTasksDependencies$ | async)!"></app-agnostic-grids>
  </section>

  <section>
    <header class="mt-4 mb-4 flex items-center justify-between">
      <h4 class="m-0 p-0">
        {{ ('reportTasks.finishedTasks' | translate | titlecase) + ' (' + (finishedTasks$ | async)?.length + ')' }}
      </h4>
      <div class="flex items-center justify-end gap-2">
        <p-button
          type="button"
          severity="danger"
          [label]="('buttons.deleteSingle' | translate) + ' (' + this.selectedTasks.length + ')'"
          [disabled]="this.selectedTasks.length <= 0"
          (click)="onDelete(selectedTasks)"
        >
          <ng-template #icon>
            <fa-icon [icon]="faTrash"></fa-icon>
          </ng-template>
        </p-button>
        <p-button
          type="button"
          [label]="('buttons.share' | translate) + ' (' + this.selectedTasks.length + ')'"
          [disabled]="shareButtonDisabled(this.selectedTasks)"
          (click)="onShare(selectedTasks)"
        >
          <ng-template #icon>
            <fa-icon [icon]="faShare"></fa-icon>
          </ng-template>
        </p-button>
        <p-button
          type="button"
          [label]="('buttons.download' | translate) + ' (' + downloadButtonTaskLength(this.selectedTasks) + ')'"
          [disabled]="downloadButtonDisabled(this.selectedTasks)"
          (click)="onDownload(selectedTasks)"
        >
          <ng-template #icon>
            <fa-icon [icon]="faDownload"></fa-icon>
          </ng-template>
        </p-button>
      </div>
    </header>
    <app-agnostic-grids
      [dependencies]="(finishedTasksDependencies$ | async)!"
      (selectionChange)="onSelectionChange($event)"
      (gridApiChange)="onGridApiChange($event)"
    ></app-agnostic-grids>
  </section>
</p-drawer>

<p-dialog
  [header]="'reportTasks.shareDialog.title' | translate"
  [modal]="true"
  [(visible)]="shareDialogVisible"
  [style]="{ width: '40rem', minHeight: '60vh', maxHeight: '95vh' }"
  [draggable]="false"
  (onHide)="onHideShareDialog()"
>
  <p-divider type="solid">
    <b>{{ 'reportTasks.shareDialog.selectedTasks' | translate }} ({{ selectedTasks.length }})</b>
  </p-divider>

  <ul class="m-0 flex w-full flex-col items-center justify-center gap-4 pb-4 pl-6">
    @for (task of firstThreeSelectedTasks(); track $index) {
      <li class="flex w-full list-none items-center justify-center gap-2">
        <p class="m-0 p-0">{{ task.reportName }}</p>
        <p-tag
          class="report-task-status"
          [ngClass]="{
            'report-task-status-strategy': task.type === TaskReportType.STRATEGY,
            'report-task-status-inspection': task.type === TaskReportType.INSPECTION,
            'report-task-status-scenario': task.type === TaskReportType.SCENARIO,
            'report-task-status-data-analysis': task.type === TaskReportType.DATA_ANALYSIS,
          }"
        >
          @switch (task.type) {
            @case (TaskReportType.STRATEGY) {
              {{ 'reportTasks.types.strategy' | translate }}
            }
            @case (TaskReportType.INSPECTION) {
              {{ 'reportTasks.types.inspection' | translate }}
            }
            @case (TaskReportType.SCENARIO) {
              {{ 'reportTasks.types.scenario' | translate }}
            }
            @case (TaskReportType.DATA_ANALYSIS) {
              {{ 'reportTasks.types.dataAnalysis' | translate }}
            }
          }
        </p-tag>
      </li>
    }
    @if (selectedTasks.length > 3) {
      <li class="list-none">...</li>
    }
  </ul>
  <p-divider type="solid">
    <b>{{ 'reportTasks.shareDialog.emails' | translate }}</b>
  </p-divider>

  <form [formGroup]="shareEmailFormGroup" class="flex flex-col gap-4 pt-4">
    <b>{{ 'fields.description' | translate }}</b>
    <quill-editor id="share-email-description" class="quillEditor w-full" formControlName="description">
      <div quill-editor-toolbar>
        <span class="ql-formats">
          <button class="ql-bold"></button>
          <button class="ql-italic"></button>
          <button class="ql-underline"></button>
          <button class="ql-strike"></button>
        </span>
        <span class="ql-formats">
          <button class="ql-list" value="ordered"></button>
          <button class="ql-list" value="bullet"></button>
        </span>
        <span class="ql-formats">
          <button class="ql-script" value="sub"></button>
          <button class="ql-script" value="super"></button>
        </span>
        <span class="ql-formats">
          <select class="ql-color"></select>
          <select class="ql-background"></select>
        </span>
        @if (emailTemplates && emailTemplates.length > 0) {
          <span class="ql-formats">
            <button (click)="openTemplatesDialog()" type="button">
              {{ 'buttons.templates' | translate }}
            </button>
          </span>
        }
      </div>
    </quill-editor>
    <p-inputgroup>
      <p-floatlabel variant="on">
        <input id="share-email" pInputText [formControl]="emailFormControl" />
        <label for="share-email">{{ 'fields.email' | translate }}</label>
      </p-floatlabel>

      <p-inputgroup-addon>
        <p-button (click)="onAddEmail()">
          <ng-template #icon>
            <fa-icon [icon]="faPlus"></fa-icon>
          </ng-template>
        </p-button>
      </p-inputgroup-addon>
    </p-inputgroup>

    @if (shareEmailFormGroup.controls.emails.value.size <= 0) {
      <p-message
        severity="info"
        [text]="'reportTasks.shareDialog.emailsInfo' | translate"
        [closable]="false"
        styleClass="flex justify-center"
      ></p-message>
    } @else {
      <div class="flex flex-wrap gap-2 pt-2">
        @for (email of shareEmailFormGroup.controls.emails.value; track $index; let i = $index) {
          <p-chip
            [label]="email"
            [removable]="i === shareEmailFormGroup.controls.emails.value.size - 1"
            (onRemove)="onRemoveEmail(email)"
          />
        }
      </div>
    }
  </form>
  <ng-template #footer>
    <div class="flex w-full justify-between gap-2 pt-2">
      <p-button severity="secondary" [label]="'buttons.cancel' | translate" (click)="onHideShareDialog()">
        <ng-template #icon>
          <fa-icon [icon]="faTimes"></fa-icon>
        </ng-template>
      </p-button>
      <p-button
        severity="primary"
        [label]="'buttons.share' | translate"
        (click)="onSubmitShareTasks()"
        [disabled]="shareEmailFormGroup.invalid"
        [loading]="loadingShareTasks()"
      >
        <ng-template #icon>
          <fa-icon [icon]="faShare"></fa-icon>
        </ng-template>
        <ng-template #loadingicon>
          <fa-icon [icon]="faSpinner" animation="spin"></fa-icon>
        </ng-template>
      </p-button>
    </div>
  </ng-template>
</p-dialog>
